import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import AiWorkspaceService, {
  WorkspaceSession,
  ConversationMessage,
  PromptConfigSafe,
  AiPromptConfigData,
} from "../../_services/AiWorkspaceService";
import "./ai-workspace.css";

const AiWorkspace: React.FC = () => {
  // URL params
  const urlParams = new URLSearchParams(window.location.search);
  const resourceId = Number(urlParams.get("resourceId")) || 0;
  const resourceName = urlParams.get("resourceName") || "AI Workspace";

  // Session state
  const [session, setSession] = useState<WorkspaceSession | null>(null);
  const [promptConfig, setPromptConfig] = useState<PromptConfigSafe | null>(
    null
  );
  const [turnsRemaining, setTurnsRemaining] = useState(0);

  // Chat state
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Reflection state
  const [reflectionText, setReflectionText] = useState("");
  const [savingReflection, setSavingReflection] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReflectionPanel, setShowReflectionPanel] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Debug panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [fullConfig, setFullConfig] = useState<AiPromptConfigData | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [lastRagInfo, setLastRagInfo] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (resourceId) {
      initSession();
    } else {
      setLoading(false);
      setError("No resourceId provided.");
    }
  }, [resourceId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const initSession = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to resume existing session
      const existingRes =
        await AiWorkspaceService.getSessionByResource(resourceId);
      if (existingRes.success && existingRes.data) {
        const { session: s, promptConfig: pc, turnsRemaining: tr } =
          existingRes.data;
        setSession(s);
        setPromptConfig(pc);
        setTurnsRemaining(tr);
        setMessages(s.conversationHistory || []);
        setReflectionText(s.reflectionText || "");

        if (s.status === "SUBMITTED") {
          setSubmitSuccess(true);
        }
        setLoading(false);
        return;
      }

      // Start new session
      const startRes = await AiWorkspaceService.startSession(resourceId);
      if (startRes.success && startRes.data) {
        setSession(startRes.data.session);
        setPromptConfig(startRes.data.promptConfig);
        setTurnsRemaining(
          startRes.data.promptConfig.maxTurns -
            (startRes.data.session.turnCount || 0)
        );
        setMessages(startRes.data.session.conversationHistory || []);
      } else {
        setError(startRes.error || startRes.message || "Failed to start session");
      }
    } catch (err) {
      setError("Failed to initialize workspace session.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!session || !inputMessage.trim() || sending) return;

    const userMsg = inputMessage.trim();
    setInputMessage("");
    setSending(true);
    setError(null);

    // Optimistically add user message
    const tempUserMsg: ConversationMessage = {
      role: "user",
      content: userMsg,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const response = await AiWorkspaceService.sendMessage(
        session.id,
        userMsg
      );
      if (response.success && response.data) {
        const assistantMsg: ConversationMessage = {
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString(),
          tokenUsage: response.data.tokenUsage,
          processingTimeMs: response.data.processingTimeMs,
        };
        setMessages((prev) => [...prev, assistantMsg]);
        setTurnsRemaining(response.data.turnsRemaining);

        // Capture RAG info for debug panel
        if (response.data.ragInfo) {
          setLastRagInfo(response.data.ragInfo);
        }

        if (
          response.data.sessionStatus === "COMPLETED" ||
          response.data.turnsRemaining <= 0
        ) {
          setSession((prev) =>
            prev ? { ...prev, status: "COMPLETED" } : prev
          );
          setShowReflectionPanel(true);
        }
      } else {
        // Remove optimistic message on error
        setMessages((prev) => prev.slice(0, -1));
        setError(response.error || "Failed to send message");
      }
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setError("Failed to send message. Please try again.");
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSaveReflection = async () => {
    if (!session) return;
    setSavingReflection(true);
    try {
      const response = await AiWorkspaceService.saveReflection(
        session.id,
        reflectionText
      );
      if (!response.success) {
        setError(response.error || "Failed to save reflection");
      }
    } catch (err) {
      setError("Failed to save reflection.");
    } finally {
      setSavingReflection(false);
    }
  };

  const handleSubmit = async () => {
    if (!session) return;

    if (
      promptConfig?.requireReflection &&
      !reflectionText.trim()
    ) {
      setError("A reflection is required before submission.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await AiWorkspaceService.submitSession(
        session.id,
        reflectionText || undefined
      );
      if (response.success) {
        setSubmitSuccess(true);
        setSession((prev) =>
          prev ? { ...prev, status: "SUBMITTED" } : prev
        );
      } else {
        setError(response.error || response.message || "Failed to submit");
      }
    } catch (err) {
      setError("Failed to submit session.");
    } finally {
      setSubmitting(false);
    }
  };

  const doResetSession = async () => {
    setLoading(true);
    setError(null);
    try {
      await AiWorkspaceService.deleteSession(session!.id);
      // Reset all state
      setSession(null);
      setPromptConfig(null);
      setMessages([]);
      setReflectionText("");
      setTurnsRemaining(0);
      setSubmitSuccess(false);
      setShowReflectionPanel(false);
      setShowDebugPanel(false);
      setFullConfig(null);
      // Re-initialize with a new session
      await initSession();
    } catch (err) {
      setError("Failed to reset session.");
      setLoading(false);
    }
  };

  const handleResetSession = () => {
    if (!session) return;
    setConfirmModal({
      message: "This will delete the current conversation and start fresh. Continue?",
      onConfirm: doResetSession
    });
  };

  const toggleDebugPanel = async () => {
    if (showDebugPanel) {
      setShowDebugPanel(false);
      return;
    }
    if (!fullConfig && resourceId) {
      setLoadingConfig(true);
      try {
        const res = await AiWorkspaceService.getConfigByResource(resourceId);
        if (res.success && res.data) {
          setFullConfig(res.data);
        }
      } catch (err) {
        // debug panel will show "could not load"
      } finally {
        setLoadingConfig(false);
      }
    }
    setShowDebugPanel(true);
  };

  // Token stats for debug panel
  const totalInputTokens = messages
    .filter((m) => m.role === "assistant" && m.tokenUsage)
    .reduce((sum, m) => sum + (m.tokenUsage?.inputTokens || 0), 0);
  const totalOutputTokens = messages
    .filter((m) => m.role === "assistant" && m.tokenUsage)
    .reduce((sum, m) => sum + (m.tokenUsage?.outputTokens || 0), 0);
  const totalProcessingTime = messages
    .filter((m) => m.role === "assistant" && m.processingTimeMs)
    .reduce((sum, m) => sum + (m.processingTimeMs || 0), 0);

  const isSessionActive =
    session?.status === "ACTIVE" || session?.status === "COMPLETED";
  const canSendMessage =
    session?.status === "ACTIVE" && turnsRemaining > 0;
  const canSubmit =
    isSessionActive && messages.length > 0 && !submitSuccess;

  if (loading) {
    return (
      <div className="ai-workspace">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="ai-workspace">
        <div className="submit-success">
          <div className="success-icon">&#10003;</div>
          <h2>Workspace Submitted</h2>
          <p>
            Your conversation and reflection have been submitted for review.
          </p>
          <div className="submit-summary">
            <div className="summary-item">
              <span className="summary-label">Messages exchanged</span>
              <span className="summary-value">{messages.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Turns used</span>
              <span className="summary-value">
                {session?.turnCount || 0}
              </span>
            </div>
          </div>
          <div className="submit-actions">
            <button
              className="btn-debug-toggle"
              onClick={toggleDebugPanel}
              title="Debug info"
              style={{ width: 40, height: 40, fontSize: 22 }}
            >
              &#9432;
            </button>
            <button className="btn-reset" onClick={handleResetSession}>
              Start New Conversation
            </button>
          </div>
          {showDebugPanel && fullConfig && (
            <div className="debug-panel-inline">
              <div className="debug-header">
                <h2>Debug Info</h2>
                <button
                  className="btn-close"
                  onClick={() => setShowDebugPanel(false)}
                >
                  &times;
                </button>
              </div>
              <div className="debug-content">
                <div className="debug-section">
                  <h3>Model</h3>
                  <div className="debug-field">
                    <span className="debug-label">Model</span>
                    <span className="debug-value">{fullConfig.model}</span>
                  </div>
                  <div className="debug-field">
                    <span className="debug-label">Temperature</span>
                    <span className="debug-value">{fullConfig.temperature}</span>
                  </div>
                  <div className="debug-field">
                    <span className="debug-label">Max tokens/response</span>
                    <span className="debug-value">{fullConfig.maxTokensPerResponse}</span>
                  </div>
                </div>
                <div className="debug-section">
                  <h3>System Prompt</h3>
                  <pre className="debug-prompt">{fullConfig.systemPrompt}</pre>
                </div>
                <div className="debug-section">
                  <h3>Session Stats</h3>
                  <div className="debug-field">
                    <span className="debug-label">Total input tokens</span>
                    <span className="debug-value">{totalInputTokens.toLocaleString()}</span>
                  </div>
                  <div className="debug-field">
                    <span className="debug-label">Total output tokens</span>
                    <span className="debug-value">{totalOutputTokens.toLocaleString()}</span>
                  </div>
                  <div className="debug-field">
                    <span className="debug-label">Total processing time</span>
                    <span className="debug-value">{(totalProcessingTime / 1000).toFixed(1)}s</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {loadingConfig && <p className="debug-loading">Loading config...</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="ai-workspace">
      <header className="workspace-header">
        <div className="header-left">
          <h1>{resourceName}</h1>
          {promptConfig && (
            <span className="turns-badge">
              {turnsRemaining} turn{turnsRemaining !== 1 ? "s" : ""} remaining
            </span>
          )}
        </div>
        <div className="header-right">
          <button
            className="btn-debug-toggle"
            onClick={toggleDebugPanel}
            title="Debug info"
          >
            &#9432;
          </button>
          <button
            className="btn-reset-small"
            onClick={handleResetSession}
            title="Reset conversation"
          >
            &#8635;
          </button>
          {session?.status === "COMPLETED" && (
            <span className="status-badge status-completed">
              Turns Complete
            </span>
          )}
          {canSubmit && (
            <button
              className="btn-submit"
              onClick={() => setShowReflectionPanel(true)}
            >
              Review &amp; Submit
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {promptConfig?.instructionsForStudent && messages.length === 0 && (
        <div className="instructions-banner">
          <h3>Instructions</h3>
          <p>{promptConfig.instructionsForStudent}</p>
        </div>
      )}

      <div className="workspace-layout">
        <div className={`chat-area ${showReflectionPanel || showDebugPanel ? "with-panel" : ""}`}>
          <div className="messages-container">
            {messages.length === 0 && (
              <div className="empty-chat">
                <p>Start the conversation by typing a message below.</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`message ${msg.role === "user" ? "message-user" : "message-assistant"}`}
              >
                <div className="message-avatar">
                  {msg.role === "user" ? "You" : "AI"}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    {msg.role === "assistant" ? (
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.processingTimeMs && (
                    <span className="message-meta">
                      {msg.processingTimeMs}ms
                    </span>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="message message-assistant">
                <div className="message-avatar">AI</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input-area">
            {canSendMessage ? (
              <div className="input-row">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                  rows={2}
                  disabled={sending}
                />
                <button
                  className="btn-send"
                  onClick={handleSendMessage}
                  disabled={sending || !inputMessage.trim()}
                >
                  {sending ? "..." : "Send"}
                </button>
              </div>
            ) : (
              <div className="input-disabled">
                {session?.status === "COMPLETED"
                  ? "You have used all your turns. Please review and submit your work."
                  : "This session is no longer accepting messages."}
              </div>
            )}
          </div>
        </div>

        {showReflectionPanel && (
          <div className="reflection-panel">
            <div className="reflection-header">
              <h2>Reflection &amp; Submission</h2>
              <button
                className="btn-close"
                onClick={() => setShowReflectionPanel(false)}
              >
                &times;
              </button>
            </div>

            {promptConfig?.reflectionPrompt && (
              <div className="reflection-prompt">
                <p>{promptConfig.reflectionPrompt}</p>
              </div>
            )}

            <div className="form-group">
              <label>
                Your Reflection
                {promptConfig?.requireReflection && (
                  <span className="required"> *</span>
                )}
              </label>
              <textarea
                value={reflectionText}
                onChange={(e) => setReflectionText(e.target.value)}
                placeholder="Write your reflection here..."
                rows={8}
              />
            </div>

            <div className="reflection-actions">
              <button
                className="btn-save-draft"
                onClick={handleSaveReflection}
                disabled={savingReflection}
              >
                {savingReflection ? "Saving..." : "Save Draft"}
              </button>
              <button
                className="btn-final-submit"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Workspace"}
              </button>
            </div>
          </div>
        )}

        {showDebugPanel && (
          <div className="debug-panel">
            <div className="debug-header">
              <h2>Debug Info</h2>
              <button
                className="btn-close"
                onClick={() => setShowDebugPanel(false)}
              >
                &times;
              </button>
            </div>
            <div className="debug-content">
              {loadingConfig ? (
                <p className="debug-loading">Loading config...</p>
              ) : fullConfig ? (
                <>
                  <div className="debug-section">
                    <h3>Model</h3>
                    <div className="debug-field">
                      <span className="debug-label">Model</span>
                      <span className="debug-value">{fullConfig.model}</span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Temperature</span>
                      <span className="debug-value">
                        {fullConfig.temperature}
                      </span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Max tokens/response</span>
                      <span className="debug-value">
                        {fullConfig.maxTokensPerResponse}
                      </span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Max turns</span>
                      <span className="debug-value">{fullConfig.maxTurns}</span>
                    </div>
                  </div>

                  <div className="debug-section">
                    <h3>System Prompt</h3>
                    <pre className="debug-prompt">{fullConfig.systemPrompt}</pre>
                  </div>

                  {fullConfig.guardrails && (
                    <div className="debug-section">
                      <h3>Guardrails</h3>
                      {fullConfig.guardrails.blockedTopics && (
                        <div className="debug-field">
                          <span className="debug-label">Blocked topics</span>
                          <span className="debug-value">
                            {fullConfig.guardrails.blockedTopics}
                          </span>
                        </div>
                      )}
                      {fullConfig.guardrails.requiredTopics && (
                        <div className="debug-field">
                          <span className="debug-label">Required topics</span>
                          <span className="debug-value">
                            {fullConfig.guardrails.requiredTopics}
                          </span>
                        </div>
                      )}
                      {fullConfig.guardrails.customRules && (
                        <div className="debug-field">
                          <span className="debug-label">Custom rules</span>
                          <span className="debug-value">
                            {fullConfig.guardrails.customRules}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="debug-section">
                    <h3>Session Stats</h3>
                    <div className="debug-field">
                      <span className="debug-label">Status</span>
                      <span className="debug-value">{session?.status}</span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Turns used</span>
                      <span className="debug-value">
                        {session?.turnCount || 0} / {fullConfig.maxTurns}
                      </span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Total input tokens</span>
                      <span className="debug-value">
                        {totalInputTokens.toLocaleString()}
                      </span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">Total output tokens</span>
                      <span className="debug-value">
                        {totalOutputTokens.toLocaleString()}
                      </span>
                    </div>
                    <div className="debug-field">
                      <span className="debug-label">
                        Total processing time
                      </span>
                      <span className="debug-value">
                        {(totalProcessingTime / 1000).toFixed(1)}s
                      </span>
                    </div>
                  </div>

                  <div className="debug-section">
                    <h3>RAG Sources (Last Message)</h3>
                    {lastRagInfo ? (
                      <>
                        <div className="debug-field">
                          <span className="debug-label">RAG enabled</span>
                          <span className="debug-value">{lastRagInfo.ragEnabled ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="debug-field">
                          <span className="debug-label">Contexts retrieved</span>
                          <span className="debug-value">{lastRagInfo.contextsRetrieved}</span>
                        </div>
                        {lastRagInfo.sources && lastRagInfo.sources.length > 0 ? (
                          <div className="debug-sources">
                            {lastRagInfo.sources.map((src: any, idx: number) => (
                              <div key={idx} className="debug-source-item">
                                <span className="debug-source-name">{src.source}</span>
                                {src.page && <span className="debug-source-page">p.{src.page}</span>}
                                <span className="debug-source-score">{(src.relevanceScore * 100).toFixed(0)}%</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="debug-loading">No documents referenced</p>
                        )}
                      </>
                    ) : (
                      <p className="debug-loading">Send a message to see RAG info</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="debug-loading">Could not load config.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal (replaces window.confirm) */}
      {confirmModal && (
        <div className="workspace-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="workspace-modal" onClick={(e) => e.stopPropagation()}>
            <p>{confirmModal.message}</p>
            <div className="workspace-modal-actions">
              <button
                className="workspace-modal-btn workspace-modal-btn-cancel"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className="workspace-modal-btn workspace-modal-btn-confirm"
                onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiWorkspace;
